import { strokeBBox, type Item, type Op, type Point, type Rect } from '@scribbleplot/shared';
import { CANVAS, DEFAULT_ACCENT } from '../tokens';
import { useCamera } from './camera';
import { useEditorUI } from './ui';
import { contentsOfFrame, hitTestRect, useBoard } from './store';

/**
 * Pointer gesture machine.
 *
 * One active gesture at a time, tracked outside React so pointer frames never
 * queue behind a render. Listeners go on `window`, so a drag keeps working when
 * the pointer leaves the canvas — the behaviour people expect from a desktop
 * tool.
 */

export type ResizeDir = 'nw' | 'ne' | 'sw' | 'se';

export type AssetDragPayload = { src: string; w: number; h: number; name: string; assetId?: string };

type Gesture =
  | { kind: 'pan'; sx: number; sy: number; camX: number; camY: number }
  | { kind: 'marquee'; x0: number; y0: number; base: string[] }
  | {
      kind: 'drag';
      /** Pointer position in world space when the drag began. */
      wx: number;
      wy: number;
      /** Starting positions, captured once so every frame is an absolute set. */
      items: Map<string, { x: number; y: number }>;
      strokes: Map<string, Point[]>;
      moved: boolean;
      /** When a click lands on an already-multi-selected item, reduce on release. */
      reduceTo: string | null;
    }
  | {
      kind: 'resize';
      dir: ResizeDir;
      id: string;
      origin: Rect;
      /** Locked aspect ratio for images; null lets width and height move freely. */
      aspect: number | null;
    }
  | { kind: 'draw' }
  | { kind: 'frame'; x0: number; y0: number }
  | { kind: 'asset'; payload: AssetDragPayload; sx: number; sy: number; moved: boolean }
  | { kind: 'minimap' };

let active: Gesture | null = null;
let ghostSetter: ((ghost: { x: number; y: number; src: string } | null) => void) | null = null;
let minimapPan: ((clientX: number, clientY: number) => void) | null = null;
/** Freehand strokes are drawn in the signed-in user's accent colour. */
let accentGetter: (() => string) | null = null;

/** The canvas registers callbacks it owns but the gesture machine must drive. */
export function registerGestureHooks(hooks: {
  setGhost: (ghost: { x: number; y: number; src: string } | null) => void;
  panMinimap: (clientX: number, clientY: number) => void;
  accent: () => string;
}): void {
  ghostSetter = hooks.setGhost;
  minimapPan = hooks.panMinimap;
  accentGetter = hooks.accent;
}

function begin(gesture: Gesture): void {
  active = gesture;
  useEditorUI.setState({ interacting: true });
  const move = (event: PointerEvent) => onMove(event);
  const finish = (event: PointerEvent) => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', finish);
    window.removeEventListener('pointercancel', finish);
    onUp(event);
    active = null;
    useEditorUI.setState({ interacting: false });
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', finish);
  window.addEventListener('pointercancel', finish);
}

/* ------------------------------------------------------------------ *
 * Gesture starters
 * ------------------------------------------------------------------ */

export function startPan(event: PointerEvent | React.PointerEvent): void {
  const { cam } = useCamera.getState();
  begin({ kind: 'pan', sx: event.clientX, sy: event.clientY, camX: cam.x, camY: cam.y });
}

export function startMarquee(wx: number, wy: number, additive: boolean): void {
  const board = useBoard.getState();
  const base = additive ? board.sel : [];
  if (!additive) board.setSel([]);
  board.setMarquee({ x: wx, y: wy, w: 0, h: 0 });
  begin({ kind: 'marquee', x0: wx, y0: wy, base });
}

export function startFrameDraft(wx: number, wy: number): void {
  useBoard.getState().setFrameDraft({ x: wx, y: wy, w: 0, h: 0 });
  begin({ kind: 'frame', x0: wx, y0: wy });
}

export function startDraw(wx: number, wy: number): void {
  useBoard.getState().setDrawPts([[wx, wy]]);
  begin({ kind: 'draw' });
}

/**
 * Begin dragging the current selection.
 *
 * Dragging a frame carries whatever sits inside it, which is what makes framed
 * areas behave like regions rather than decoration.
 */
export function startDrag(anchor: Item, sel: string[], wx: number, wy: number, reduceTo: string | null): void {
  const board = useBoard.getState();
  const doc = board.doc;

  const items = new Map<string, { x: number; y: number }>();
  const strokes = new Map<string, Point[]>();

  for (const id of sel) {
    const item = doc.items.find((it) => it.id === id);
    if (item) items.set(id, { x: item.x, y: item.y });
    const stroke = doc.strokes.find((s) => s.id === id);
    if (stroke) strokes.set(id, stroke.pts);
  }

  if (anchor.type === 'frame') {
    const inside = contentsOfFrame(doc, anchor);
    for (const id of inside.items) {
      if (items.has(id)) continue;
      const item = doc.items.find((it) => it.id === id);
      if (item) items.set(id, { x: item.x, y: item.y });
    }
    for (const id of inside.strokes) {
      if (strokes.has(id)) continue;
      const stroke = doc.strokes.find((s) => s.id === id);
      if (stroke) strokes.set(id, stroke.pts);
    }
  }

  board.beginGesture();
  begin({ kind: 'drag', wx, wy, items, strokes, moved: false, reduceTo });
}

export function startResize(dir: ResizeDir, item: Item): void {
  useBoard.getState().beginGesture();
  begin({
    kind: 'resize',
    dir,
    id: item.id,
    origin: { x: item.x, y: item.y, w: item.w, h: item.h },
    // Images keep their proportions; everything else resizes freely.
    aspect: item.type === 'image' ? item.w / item.h : null,
  });
}

export function startAssetDrag(payload: AssetDragPayload, event: React.PointerEvent): void {
  begin({ kind: 'asset', payload, sx: event.clientX, sy: event.clientY, moved: false });
}

export function startMinimapPan(event: React.PointerEvent): void {
  minimapPan?.(event.clientX, event.clientY);
  begin({ kind: 'minimap' });
}

/* ------------------------------------------------------------------ *
 * Movement
 * ------------------------------------------------------------------ */

function onMove(event: PointerEvent): void {
  const gesture = active;
  if (!gesture) return;
  const camera = useCamera.getState();
  const board = useBoard.getState();

  switch (gesture.kind) {
    case 'pan':
      camera.set({
        ...camera.cam,
        x: gesture.camX + event.clientX - gesture.sx,
        y: gesture.camY + event.clientY - gesture.sy,
      });
      return;

    case 'minimap':
      minimapPan?.(event.clientX, event.clientY);
      return;

    case 'asset': {
      if (Math.hypot(event.clientX - gesture.sx, event.clientY - gesture.sy) > 4) gesture.moved = true;
      if (gesture.moved) ghostSetter?.({ x: event.clientX, y: event.clientY, src: gesture.payload.src });
      return;
    }
  }

  const [wx, wy] = camera.toWorld(event.clientX, event.clientY);

  switch (gesture.kind) {
    case 'marquee':
      board.setMarquee({
        x: Math.min(gesture.x0, wx),
        y: Math.min(gesture.y0, wy),
        w: Math.abs(wx - gesture.x0),
        h: Math.abs(wy - gesture.y0),
      });
      return;

    case 'frame':
      board.setFrameDraft({
        x: Math.min(gesture.x0, wx),
        y: Math.min(gesture.y0, wy),
        w: Math.abs(wx - gesture.x0),
        h: Math.abs(wy - gesture.y0),
      });
      return;

    case 'draw': {
      const pts = board.drawPts ?? [];
      const last = pts[pts.length - 1];
      // Drop samples that are closer than a world unit; they add nothing but
      // path length and make the smoothing wobble.
      if (!last || Math.hypot(wx - last[0], wy - last[1]) > 1.2) board.setDrawPts([...pts, [wx, wy]]);
      return;
    }

    case 'drag': {
      const dx = wx - gesture.wx;
      const dy = wy - gesture.wy;
      if (!gesture.moved) {
        if (Math.hypot(dx * camera.cam.s, dy * camera.cam.s) <= CANVAS.dragThreshold) return;
        gesture.moved = true;
      }
      const ops: Op[] = [];
      if (gesture.items.size) {
        ops.push({
          t: 'set',
          c: 'items',
          p: [...gesture.items].map(([id, start]) => ({ id, patch: { x: start.x + dx, y: start.y + dy } })),
        });
      }
      if (gesture.strokes.size) {
        ops.push({
          t: 'set',
          c: 'strokes',
          p: [...gesture.strokes].map(([id, pts]) => ({
            id,
            patch: { pts: pts.map(([x, y]) => [x + dx, y + dy]) },
          })),
        });
      }
      if (ops.length) board.gestureOps(ops);
      return;
    }

    case 'resize': {
      const { dir, origin, aspect } = gesture;
      let { x, y } = origin;
      let w = origin.w;
      let h = origin.h;

      if (dir.includes('e')) w = wx - origin.x;
      if (dir.includes('s')) h = wy - origin.y;
      if (dir.includes('w')) {
        w = origin.x + origin.w - wx;
        x = wx;
      }
      if (dir.includes('n')) {
        h = origin.y + origin.h - wy;
        y = wy;
      }

      w = Math.max(CANVAS.minItemW, w);
      h = Math.max(CANVAS.minItemH, h);

      if (aspect) {
        h = w / aspect;
        // Dragging a north handle grows upward, so the bottom edge must stay put.
        if (dir.includes('n')) y = origin.y + origin.h - h;
      }
      if (dir.includes('w')) x = Math.min(x, origin.x + origin.w - CANVAS.minItemW);
      if (dir.includes('n') && !aspect) y = Math.min(y, origin.y + origin.h - CANVAS.minItemH);

      board.gestureOps([{ t: 'set', c: 'items', p: [{ id: gesture.id, patch: { x, y, w, h } }] }]);
      return;
    }
  }
}

/* ------------------------------------------------------------------ *
 * Completion
 * ------------------------------------------------------------------ */

function onUp(event: PointerEvent): void {
  const gesture = active;
  if (!gesture) return;
  const board = useBoard.getState();
  const camera = useCamera.getState();

  switch (gesture.kind) {
    case 'marquee': {
      const rect = board.marquee;
      let sel = gesture.base;
      if (rect && (rect.w > 4 || rect.h > 4)) {
        sel = [...new Set([...gesture.base, ...hitTestRect(board.doc, rect)])];
      }
      board.setMarquee(null);
      board.setSel(sel);
      return;
    }

    case 'drag':
      // A click that never moved and landed inside a multi-selection narrows it.
      if (!gesture.moved && gesture.reduceTo) board.setSel([gesture.reduceTo]);
      board.endGesture();
      return;

    case 'resize':
      board.endGesture();
      return;

    case 'frame': {
      const draft = board.frameDraft;
      // A click rather than a drag still deserves a frame, centred on the point.
      const rect =
        draft && draft.w > 40 && draft.h > 40
          ? draft
          : { x: (draft?.x ?? 0) - 240, y: (draft?.y ?? 0) - 170, w: 480, h: 340 };
      board.setFrameDraft(null);
      board.addFrame(rect);
      return;
    }

    case 'draw': {
      const pts = board.drawPts;
      board.setDrawPts(null);
      if (pts && pts.length > 1) board.addStroke(pts, accentGetter?.() ?? DEFAULT_ACCENT);
      return;
    }

    case 'asset': {
      const { payload } = gesture;
      ghostSetter?.(null);
      const viewport = camera.viewport;
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();

      if (gesture.moved) {
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) return;
        const [wx, wy] = camera.toWorld(event.clientX, event.clientY);
        board.addImage(payload.src, payload.w, payload.h, wx, wy, payload.name, payload.assetId);
        return;
      }

      // A plain click drops the asset at the centre of the view.
      const [wx, wy] = camera.toWorld(rect.left + rect.width / 2, rect.top + rect.height / 2);
      board.addImage(payload.src, payload.w, payload.h, wx, wy, payload.name, payload.assetId);
      return;
    }
  }
}
